<script setup>
const fullWidthMode = inject('fullWidthMode', false);
const halfCol = computed(() => (fullWidthMode ? undefined : 6));

import ImgBalance from '@manseImages/yongsin-light/img-yongsin-balance.png';
import IconBattery from '@manseImages/yongsin-light/icon-charging-battery.png';
import IconTrust from '@manseImages/yongsin-light/icon-trust.png';
import IconClew from '@manseImages/yongsin-light/icon-clew.png';
import IconArrowDown from '@manseImages/yongsin-light/icon-arrow-down.png';

// 오른쪽 페이지 관계도 화살표 에셋
import ArrowYongsin from '@manseImages/yongsin-light/arrow-yongsin.svg?url';
import ArrowHeesin from '@manseImages/yongsin-light/arrow-heesin.svg?url';

// 오른쪽 페이지 에셋
import IconOakTree from '@manseImages/yongsin-light/icon-oak-tree.png';
import IconFire from '@manseImages/yongsin-light/icon-fire.png';
import IconMountain from '@manseImages/yongsin-light/icon-mountain.png';
import IconBattle from '@manseImages/yongsin-light/icon-battle.png';
import IconWater from '@manseImages/yongsin-light/icon-water.png';
import IconStar from '@manseImages/yongsin-light/icon-star.png';
import IconGarden from '@manseImages/yongsin-light/icon-garden.png';
import ImgCat from '@manseImages/yongsin-light/img-yongsin-cat.png';
import ImgCatSmall from '@manseImages/yongsin-light/img-yongsin-cat-small.png';
import ImgBears from '@manseImages/yongsin-light/img-yongsin-bears.png';


const props = defineProps({
  isMobile: Boolean,
});

const emit = defineEmits(['update:activeTab']);

// 오른쪽 페이지 탭 상태
const activeTab = ref('why'); // 'why' | 'more'
watch(activeTab, (val) => emit('update:activeTab', val), { immediate: true });

// 오른쪽 페이지 스크롤 컨테이너 ref
const rightContentRef = ref(null);

// "한 가지 더 →" 버튼 클릭 핸들러
const handleOneMore = () => {
  activeTab.value = 'more';
  nextTick(() => {
    if (props.isMobile) {
      const inner = document.querySelector('.pop-ana-saju-reading-inner');
      if (inner) inner.scrollTop = 0;
      window.scrollTo(0, 0);
    } else {
      rightContentRef.value?.scrollTo({ top: 0, behavior: 'smooth' });
    }
  });
};

// 오행 아이콘 + 막대 높이 + 색상 (Figma 기준, 연한 톤)
const fiveElements = [
  { name: '목', icon: IconOakTree, barHeight: 127, color: 'rgba(94, 159, 28, 0.5)' },
  { name: '화', icon: IconFire, barHeight: 75, color: 'rgba(194, 53, 53, 0.5)' },
  { name: '토', icon: IconMountain, barHeight: 85, color: 'rgba(219, 152, 35, 0.5)' },
  { name: '금', icon: IconBattle, barHeight: 73, color: 'rgba(148, 148, 148, 0.5)' },
  { name: '수', icon: IconWater, barHeight: 135, color: 'rgba(64, 64, 64, 0.5)' },
];
</script>

<template>
  <VRow class="full-height">
    <!-- ═══ 왼쪽 페이지 ═══ -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-first">
      <VCard class="ysl-card full-height right">
        <!-- 타이틀 바 -->
        <div class="ysl-title-bar">
          <span class="ysl-title-bar__emoji">📖</span>
          <span class="ysl-title-bar__text">용신이란 무엇일까요?</span>
        </div>

        <!-- 본문 영역 -->
        <div class="ysl-content">
          <div class="ysl-section">
            <!-- 도입 문구 -->
            <p class="ysl-heading">🍀 사람마다 편하게 잘 풀리는 방식이 다릅니다.</p>

            <div class="ysl-text-block">
              <p>어떤 사람은 차근차근 나아갈 때 안정감을 느끼고,</p>
              <p>어떤 사람은 빠르게 움직일 때 더 힘이 나기도 하죠.</p>
            </div>

            <p class="ysl-text-single">사주에서도 마찬가지입니다.</p>

            <div class="ysl-text-block">
              <p>누군가는 불의 기운이 강하고, 누군가는 물의 기운이 많고,</p>
              <p>어떤 사람은 특정 기운이 부족하기도 합니다.</p>
            </div>

            <!-- 일러스트 이미지 -->
            <div class="ysl-illust">
              <img :src="ImgBalance" alt="오행 균형 일러스트" />
            </div>

            <!-- 용신 설명 -->
            <div class="ysl-text-block">
              <p>이렇게 내게 부족한 부분을 채워주고, 전체 균형을 맞춰주는 기운을</p>
              <p>
                사주에서는
                <span class="ysl-text-bold ysl-text-highlight">'용신(用神)'</span>
                이라고 합니다.
              </p>
            </div>

            <!-- 쉽게 말하면 -->
            <p class="ysl-heading">👍 쉽게 말하면</p>

            <!-- 3개 아이콘 카드 -->
            <div class="ysl-icon-cards">
              <div class="ysl-icon-card">
                <div class="ysl-icon-card__img-wrap">
                  <img :src="IconBattery" alt="에너지" />
                </div>
                <div class="ysl-icon-card__label">
                  <span>힘이 잘 나는</span>
                  <span class="ysl-icon-card__highlight" style="--hl-color: #ddf6bc">에너지</span>
                </div>
              </div>
              <div class="ysl-icon-card">
                <div class="ysl-icon-card__img-wrap">
                  <img :src="IconTrust" alt="안정감" />
                </div>
                <div class="ysl-icon-card__label">
                  <span>
                    <span class="ysl-icon-card__highlight" style="--hl-color: #ffc3bf">안정감</span>
                    을
                  </span>
                  <span>느끼는 방향</span>
                </div>
              </div>
              <div class="ysl-icon-card">
                <div class="ysl-icon-card__img-wrap">
                  <img :src="IconClew" alt="풀리는 기운" />
                </div>
                <div class="ysl-icon-card__label">
                  <span>자연스럽게</span>
                  <span class="ysl-icon-card__highlight" style="--hl-color: #d3ebff"
                    >풀리는 기운</span
                  >
                </div>
              </div>
            </div>

            <!-- 화살표 -->
            <div class="ysl-arrow">
              <svg
                width="30"
                height="30"
                viewBox="0 0 30.2586 29.6484"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <rect x="9.97949" width="10.298" height="17.8106" rx="1" fill="#5D81BF" />
                <path
                  d="M15.8661 28.8461C15.4699 29.2777 14.7891 29.2777 14.3929 28.8461L3.56612 17.0534C2.97742 16.4122 3.43228 15.3771 4.30275 15.3771L25.9562 15.3771C26.8267 15.3771 27.2816 16.4122 26.6929 17.0534L15.8661 28.8461Z"
                  fill="#5D81BF"
                />
              </svg>
            </div>

            <!-- 결론 문구 -->
            <p class="ysl-conclusion">
              <span class="ysl-conclusion__quote">「 나를 더 편안하게 만들어주는 방향 」</span>
              <span class="ysl-conclusion__suffix"> 입니다.</span>
            </p>
          </div>
        </div>
      </VCard>
    </VCol>

    <!-- ═══ 오른쪽 페이지 ═══ -->
    <VCol cols="12" :md="halfCol" class="no-pad-col mobile-second">
      <VCard class="ysl-card full-height">
        <div ref="rightContentRef" class="ysl-content">
          <!-- 탭 버튼 -->
          <div class="ysl-tabs">
            <button
              class="ysl-tab"
              :class="{ active: activeTab === 'why' }"
              @click="activeTab = 'why'"
            >
              왜 용신을 볼까요?
            </button>
            <button
              class="ysl-tab"
              :class="{ active: activeTab === 'more' }"
              @click="activeTab = 'more'"
            >
              한 가지 더!
            </button>
          </div>

          <!-- ── 왜 용신을 볼까요? 탭 ── -->
          <div v-if="activeTab === 'why'" class="ysl-section">
            <p class="ysl-text-block">
              사주는 목 · 화 · 토 · 금 · 수 <span class="ysl-text-bold">다섯 가지 기운</span>으로
              이루어져 있습니다.
            </p>

            <!-- 오행 아이콘 차트 -->
            <div class="ysl-five-elements">
              <div class="ysl-five-elements__item" v-for="el in fiveElements" :key="el.name">
                <div
                  class="ysl-five-elements__bar"
                  :style="{ height: el.barHeight + 'px', background: el.color }"
                ></div>
                <img :src="el.icon" :alt="el.name" class="ysl-five-elements__icon" />
              </div>
            </div>

            <div class="ysl-text-block">
              <p>이 기운이 균형을 이루면 안정적이지만</p>
              <p>한쪽이 강하거나 부족하면 컨디션이나 선택에서 영향을 줄 수 있습니다.</p>
            </div>

            <!-- 그래서 인용 -->
            <div class="ysl-quote-section">
              <p class="ysl-text-block">그래서</p>
              <div class="ysl-quote-block">
                <svg
                  class="ysl-quote-mark left"
                  width="18"
                  height="14"
                  viewBox="0 0 18 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M7.2 0L4.32 7H7.2V14H0V7L4.32 0H7.2ZM18 0L15.12 7H18V14H10.8V7L15.12 0H18Z"
                    fill="#4E80D6"
                  />
                </svg>
                <div class="ysl-quote-text">
                  <p><span class="ysl-text-semibold">부족한 부분을 채워주고</span></p>
                  <p>
                    <span class="ysl-text-semibold">과한 부분을 조절해주는 방향을 찾기 위해</span>
                  </p>
                </div>
                <svg
                  class="ysl-quote-mark right"
                  width="18"
                  height="14"
                  viewBox="0 0 18 14"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M10.8 14L13.68 7H10.8V0H18V7L13.68 14H10.8ZM0 14L2.88 7H0V0H7.2V7L2.88 14H0Z"
                    fill="#4E80D6"
                  />
                </svg>
              </div>
              <p class="ysl-text-block">용신을 찾아봅니다.</p>
            </div>

            <!-- 어떻게 활용하나요? -->
            <div class="ysl-usage">
              <p class="ysl-heading">💡 어떻게 활용하나요?</p>
              <div class="ysl-text-block">
                <p>내 삶을 조금 더 편안하게 만드는 방향과 기준으로</p>
                <p>다양하게 활용할 수 있습니다.</p>
              </div>

              <!-- 말풍선 카드들 -->
              <div class="ysl-speech-cards">
                <div class="ysl-speech-card left">
                  <img :src="IconStar" alt="" class="ysl-speech-card__deco deco-left" />
                  <div class="ysl-speech-card__bubble">
                    <span class="ysl-speech-card__tail tail-left"></span>
                    <p class="ysl-speech-card__text ysl-text-bold">중요한 결정을 앞두고 있을 때</p>
                  </div>
                </div>
                <div class="ysl-speech-card right">
                  <div class="ysl-speech-card__bubble bubble-right">
                    <span class="ysl-speech-card__tail tail-right"></span>
                    <p class="ysl-speech-card__text ysl-text-bold">생활 속에서 좋은 흐름을 유지하고 싶을 때</p>
                  </div>
                  <img :src="IconGarden" alt="" class="ysl-speech-card__deco deco-right" />
                </div>
              </div>

              <div class="ysl-text-block">
                <p>색상, 방향, 시간, 숫자, 행동 등</p>
                <p>
                  <span class="ysl-text-semibold">생활 속에서 자연스럽게 활용</span>할 수 있습니다.
                </p>
              </div>
            </div>

            <!-- 한 가지 더 버튼 -->
            <button class="ysl-one-more-btn" @click="handleOneMore">
              한 가지 더 →
            </button>

            <!-- 결론 카드 -->
            <div class="ysl-bottom-card">
              <img :src="ImgCat" alt="" class="ysl-bottom-card__img" />
              <div class="ysl-bottom-card__text">
                <p class="ysl-bottom-card__title">낭만 용신 라이트를 통해</p>
                <div class="ysl-bottom-card__desc">
                  <p>나에게 힘이 되는 기운을 살펴보면서</p>
                  <p>조금 더 안정적인 나만의 계획을 만들어보세요.</p>
                </div>
              </div>
            </div>
          </div>

          <!-- ── 한 가지 더! 탭 ── -->
          <div v-if="activeTab === 'more'" class="ysl-section">
            <div class="ysl-section">
              <!-- 희신 설명 -->
              <div class="ysl-heesin-intro">
                <p class="ysl-text-block">
                  용신을 도와주는 기운을
                  <span class="ysl-text-semibold ysl-text-highlight">'희신(喜神)'</span> 이라고
                  합니다.
                </p>
                <div class="ysl-text-block">
                  <p>
                    용신이 중심이라면 희신은 그
                    <span class="ysl-text-medium">중심이 더 잘 유지될 수 있도록</span>
                  </p>
                  <p><span class="ysl-text-medium">옆에서 받쳐주는 역할</span>을 합니다.</p>
                </div>
              </div>

              <!-- 곰돌이 이미지 -->
              <div class="ysl-illust">
                <img :src="ImgBears" alt="용신과 희신" />
              </div>

              <div class="ysl-text-block">
                <p>용신이 방향을 잡아준다면,</p>
                <p>
                  희신은 그 방향을 <span class="ysl-text-medium">더 안정적으로 이어갈 수 있게</span>
                </p>
                <p><span class="ysl-text-medium">도와주는 흐름</span>이라고 볼 수 있습니다.</p>
              </div>

              <!-- 용신-희신 관계도 (CSS) -->
              <div class="ysl-relation">
                <div class="ysl-relation__diagram">
                  <!-- 중심 기운 라벨 + 화살표 -->
                  <div class="ysl-relation__label label-left">
                    <img class="ysl-relation__arrow arrow-left" :src="ArrowYongsin" alt="" />
                    <span class="ysl-relation__label-text label-text-left">중심 기운</span>
                  </div>

                  <!-- 용신 큰 원 -->
                  <div class="ysl-relation__circle circle-yongsin">
                    <span>용신</span>
                  </div>

                  <!-- 희신 작은 원 -->
                  <div class="ysl-relation__circle circle-heesin">
                    <span>희신</span>
                  </div>

                  <!-- 보조 기운 라벨 + 화살표 -->
                  <div class="ysl-relation__label label-right">
                    <img class="ysl-relation__arrow arrow-right" :src="ArrowHeesin" alt="" />
                    <span class="ysl-relation__label-text label-text-right">보조 기운</span>
                  </div>
                </div>
              </div>

              <div class="ysl-text-block">
                <p>이 두 가지 흐름을 함께 보면</p>
                <p>내 삶의 균형을 더 쉽게 이해할 수 있습니다.</p>
              </div>

              <div class="ysl-text-block">
                <p>특히 용신을 잘 활용하고 있을 때</p>
                <p>희신의 기운이 더해지면</p>
                <p>
                  <span class="ysl-text-medium">전체 흐름이 한 층 더 안정적</span>으로 이어질 수
                  있습니다.
                </p>
              </div>

              <!-- 결론 카드 -->
              <div class="ysl-bottom-card">
                <img :src="ImgCatSmall" alt="" class="ysl-bottom-card__img" />
                <div class="ysl-bottom-card__text">
                  <p class="ysl-bottom-card__title">낭만 용신 라이트를 통해</p>
                  <div class="ysl-bottom-card__desc">
                    <p>나에게 힘이 되는 기운을 살펴보면서</p>
                    <p>조금 더 안정적인 나만의 계획을 만들어보세요.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </VCard>
    </VCol>
  </VRow>
</template>

<style scoped lang="scss">
$ysl-primary: #4e80d6;
$ysl-primary-light: rgba(78, 128, 214, 0.05);
$ysl-primary-highlight: rgba(78, 128, 214, 0.2);
$ysl-text: #333;

.ysl-card {
  background: #fff;
  border-radius: 0 !important;
  box-shadow: none !important;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  &.right {
    border-right: 1px solid #ebebeb;
  }
}

// ── 타이틀 바 ──
.ysl-title-bar {
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 24px;
  background: $ysl-primary-light;
  flex-shrink: 0;

  &__emoji {
    font-size: 12px;
    line-height: 18px;
  }

  &__text {
    color: #5d81bf;
    font-family: Pretendard;
    font-size: 16px;
    font-weight: 700;
    line-height: 24px;
  }
}

// ── 본문 영역 ──
.ysl-content {
  flex: 1;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 24px;
}

.ysl-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
}

// ── 텍스트 ──
.ysl-heading {
  width: 100%;
  color: $ysl-text;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 700;
  line-height: 24px;
  margin: 0;
}

.ysl-text-block {
  width: 100%;
  text-align: center;
  color: $ysl-text;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 400;
  line-height: 24px;

  p {
    margin: 0;
  }
}

.ysl-text-single {
  width: 100%;
  text-align: center;
  color: $ysl-text;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 400;
  line-height: 24px;
  margin: 0;
}

.ysl-text-bold {
  font-weight: 600;
}

// ── 일러스트 ──
.ysl-illust {
  display: flex;
  justify-content: center;

  img {
    width: 291px;
    max-width: 100%;
    height: auto;
    object-fit: contain;
  }
}

// ── 아이콘 카드 ──
.ysl-icon-cards {
  display: flex;
  gap: 24px;
  justify-content: center;
  align-items: center;
  width: 100%;
  flex-wrap: nowrap;

  @media (max-width: 400px) {
    gap: 10px;
  }
}

.ysl-icon-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 103px;
  min-width: 0;
  flex-shrink: 1;
  padding: 25px 15px;
  border-radius: 15px;
  background: #fff;

  @media (max-width: 400px) {
    padding: 20px 10px;
    gap: 12px;
  }

  &__img-wrap {
    width: 60px;
    height: 60px;

    img {
      width: 100%;
      height: 100%;
      object-fit: contain;
    }
  }

  &__label {
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    color: $ysl-text;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 500;
    line-height: 1.4;

    span {
      display: inline;
    }
  }

  &__highlight {
    font-weight: 700;
    position: relative;
    display: inline;
    isolation: isolate;

    &::after {
      content: '';
      position: absolute;
      left: 0;
      bottom: 0;
      width: 100%;
      height: 10px;
      background: var(--hl-color, $ysl-primary-highlight);
      z-index: -1;
    }
  }
}

// ── 화살표 ──
.ysl-arrow {
  display: flex;
  justify-content: center;

  img {
    width: 30px;
    height: auto;
  }
}

// ── 결론 문구 ──
.ysl-conclusion {
  text-align: center;
  margin: 0;

  &__quote {
    color: $ysl-text;
    font-family: Pretendard;
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
  }

  &__suffix {
    color: $ysl-text;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;
  }
}

// ═══════════════════════════════
// 오른쪽 페이지 스타일
// ═══════════════════════════════

// ── 탭 ──
.ysl-tabs {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
}

.ysl-tab {
  padding: 6px 12px;
  border-radius: 16px;
  border: none;
  cursor: pointer;
  font-family: Pretendard;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
  color: #757575;
  background: #f8f8f8;
  transition: all 0.2s;

  &.active {
    background: $ysl-primary;
    color: #fff;
    font-weight: 700;
  }
}

// ── 공통 텍스트 유틸 ──
.ysl-text-semibold {
  font-weight: 600;
}

.ysl-text-medium {
  font-weight: 500;
}

.ysl-text-highlight {
  position: relative;
  isolation: isolate;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    bottom: 2px;
    width: 100%;
    height: 10px;
    background: $ysl-primary-highlight;
    z-index: -1;
  }
}

// ── 오행 아이콘 차트 ──
.ysl-five-elements {
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 20px;
  padding-top: 10px;
  height: 175px;

  &__item {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0;
  }

  &__bar {
    width: 1px;
    border-radius: 0;
  }

  &__icon {
    width: 60px;
    height: 60px;
    object-fit: contain;
    margin-top: -26px;
  }
}

// ── 인용 블록 ──
.ysl-quote-section {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

.ysl-quote-block {
  display: flex;
  align-items: flex-start;
  justify-content: center;
  gap: 5px;
}

.ysl-quote-mark {
  flex-shrink: 0;
  width: 18px;
  height: 14px;

  &.left {
    align-self: flex-start;
    margin-top: 5px;
  }

  &.right {
    align-self: flex-start;
    margin-top: 5px;
  }
}

.ysl-quote-text {
  text-align: center;
  color: $ysl-text;
  font-family: Pretendard;
  font-size: 15px;
  line-height: 1.5;

  p {
    margin: 0;
  }
}

// ── 활용법 ──
.ysl-usage {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  width: 100%;
}

// ── 말풍선 카드 ──
.ysl-speech-cards {
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  max-width: 438px;
  margin-top: 8px;
  margin-inline: auto;
  overflow: visible;
}

.ysl-speech-card {
  position: relative;
  display: flex;
  align-items: flex-start;

  &.left {
    align-self: flex-start;
    padding-left: 10px;
  }

  &.right {
    align-self: flex-end;
  }

  &__bubble {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 18px 32px;
    border-radius: 34px;
    background: #FFD8D8;
    box-shadow: 1px 3px 2px rgba(196, 121, 121, 1);

    &.bubble-right {
      background: #FFF1B9;
      box-shadow: 1px 3px 2px rgba(213, 191, 104, 1);
    }
  }

  &__tail {
    position: absolute;
    bottom: -10px;
    width: 0;
    height: 0;

    &.tail-left {
      left: 56px;
      border-left: 8px solid transparent;
      border-right: 8px solid transparent;
      border-top: 12px solid #FFD8D8;
      transform: skewX(-15deg);
    }

    &.tail-right {
      right: 40px;
      border-left: 8px solid transparent;
      border-right: 8px solid transparent;
      border-top: 12px solid #FFF1B9;
      transform: skewX(15deg);
    }
  }

  &__text {
    margin: 0;
    font-family: Pretendard;
    font-size: 15px;
    line-height: 1.4;
    color: $ysl-text;
    word-break: keep-all;
  }

  &__deco {
    width: 65px;
    height: 65px;
    object-fit: contain;
    flex-shrink: 0;
    position: absolute;

    &.deco-left {
      left: -20px;
      top: -34px;
      transform: rotate(13.6deg);
      z-index: 3;
    }

    &.deco-right {
      right: -10px;
      top: -47px;
      z-index: 3;
    }
  }
}

// ── 한 가지 더 버튼 ──
.ysl-one-more-btn {
  width: 330px;
  max-width: 100%;
  padding: 14px 20px;
  border: none;
  border-radius: 100px;
  background: $ysl-primary;
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.25);
  color: #fff;
  font-family: Pretendard;
  font-size: 15px;
  font-weight: 700;
  line-height: 20px;
  text-align: center;
  cursor: pointer;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.9;
  }
}

// ── 하단 결론 카드 ──
.ysl-bottom-card {
  display: flex;
  align-items: center;
  gap: 20px;
  width: 100%;
  padding: 20px 24px;
  border-radius: 12px;
  background: $ysl-primary-light;

  &__img {
    width: 110px;
    height: 110px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;

    @media (max-width: 560px) {
      width: 80px;
      height: 80px;
    }
  }

  &__text {
    display: flex;
    flex-direction: column;
    gap: 8px;
    flex: 1;
    min-width: 0;
  }

  &__title {
    margin: 0;
    color: $ysl-primary;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 700;
    line-height: 24px;
  }

  &__desc {
    color: $ysl-text;
    font-family: Pretendard;
    font-size: 15px;
    font-weight: 400;
    line-height: 24px;

    p {
      margin: 0;
    }
  }
}

// ── 희신 소개 ──
.ysl-heesin-intro {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
}

// ── 용신-희신 관계도 (CSS) ──
.ysl-relation {
  display: flex;
  justify-content: center;
  padding: 10px 0;

  &__diagram {
    position: relative;
    width: 362px;
    max-width: 100%;
    height: 120px;
  }

  &__circle {
    position: absolute;
    border-radius: 200px;
    border: 1px solid rgba(78, 128, 214, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;

    span {
      color: #4e80d6;
      font-family: Pretendard;
      font-weight: 700;
      text-align: center;
    }

    &.circle-yongsin {
      width: 120px;
      height: 120px;
      top: 0;
      left: 84px;
      background: rgba(78, 128, 214, 0.05);
      animation: ysl-circle-appear 0.6s ease-out both;

      span {
        font-size: 18px;
        line-height: 24px;
      }
    }

    &.circle-heesin {
      width: 90px;
      height: 90px;
      top: 15px;
      left: 192px;
      animation: ysl-circle-appear 0.6s ease-out 0.3s both;

      span {
        font-size: 16px;
        line-height: 24px;
      }
    }
  }

  &__label {
    position: absolute;

    &.label-left {
      left: 20px;
      top: 39px;
      display: flex;
      flex-direction: column;
      align-items: center;
      animation: ysl-fade-in 0.5s ease-out 0.8s both;
    }

    &.label-right {
      left: 277px;
      top: 74px;
      display: flex;
      flex-direction: column;
      align-items: center;
      animation: ysl-fade-in 0.5s ease-out 1.1s both;
    }
  }

  &__arrow {
    display: inline-block;
    object-fit: contain;

    &.arrow-left {
      width: 42px;
      height: 17px;
    }

    &.arrow-right {
      width: 26px;
      height: 11px;
      transform: scaleY(-1) rotate(180deg);
    }
  }

  &__label-text {
    font-family: 'GyeonggiTitle', Pretendard, sans-serif;
    font-weight: 500;
    white-space: nowrap;
    text-align: center;

    &.label-text-left {
      color: #4e80d6;
      font-size: 16px;
      line-height: 24px;
    }

    &.label-text-right {
      color: #5878af;
      font-size: 15px;
      line-height: 24px;
    }
  }
}

// ── 애니메이션 ──
@keyframes ysl-circle-appear {
  0% {
    opacity: 0;
    transform: scale(0.5);
  }
  100% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes ysl-fade-in {
  0% {
    opacity: 0;
  }
  100% {
    opacity: 1;
  }
}
</style>
